/**
 * <gj-cart> (handoff v2 §E/§F): minicart and cart page content.
 * - Quantity / remove buttons ([data-gj-line] + [data-gj-quantity]) → /cart/change.js with the Section Rendering API
 *   (`sections` = data-sections), then the element is replaced by the fresh HTML.
 * - Gift wrap checkbox ([data-gj-gift]) adds or removes the gift wrap variant; discount form ([data-gj-discount])
 *   applies a code through /cart/update.js.
 * - Every change is announced with Horizon's CartLinesUpdateEvent, so the header count updates.
 * - Adds made elsewhere (product page, quick add, Shop the look) re-render the minicart and open it.
 * - Engraving fee: the hidden "Incisione" product (data-engraving-variant, theme setting) always has as many units as the
 *   cart lines with a non-empty "Incisione" property: added after an engraved add, adjusted or removed after a change.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SOURCE = 'gj-cart';

/** @param {string} key */
const route = (key) => /** @type {any} */ (window).Theme?.routes?.[key];

class GjCart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('submit', this.#onSubmit);
    // Only one listener per page: the first <gj-cart> refreshes all of them
    if (!GjCart.listening) {
      GjCart.listening = true;
      document.addEventListener(StandardEvents.cartLinesUpdate, GjCart.onExternalUpdate);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('submit', this.#onSubmit);
  }

  static listening = false;

  /**
   * Align the engraving fee line with the engraved lines of the cart.
   * @returns {Promise<boolean>} whether the cart was changed
   */
  static async syncEngravingFee() {
    const variant = Number(document.querySelector('gj-cart[data-engraving-variant]')?.getAttribute('data-engraving-variant'));
    if (!variant) return false;
    const cart = await fetch(`${route('cart_url') ?? '/cart'}.js`).then((r) => r.json());
    const wanted = cart.items
      .filter((/** @type {any} */ item) => item.variant_id !== variant && String(item.properties?.Incisione ?? '').trim())
      .reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + item.quantity, 0);
    const have = cart.items.find((/** @type {any} */ item) => item.variant_id === variant)?.quantity ?? 0;
    if (wanted === have) return false;
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (have === 0) {
      await fetch(route('cart_add_url') ?? '/cart/add.js', { method: 'POST', headers, body: JSON.stringify({ id: variant, quantity: wanted }) });
    } else {
      await fetch(route('cart_change_url') ?? '/cart/change.js', { method: 'POST', headers, body: JSON.stringify({ id: String(variant), quantity: wanted }) });
    }
    return true;
  }

  /**
   * Re-render all carts after an add made outside them, then open the minicart.
   * The drawer opens only once the fresh HTML is in place and its thumbnails are decoded: opening first and swapping
   * the content mid-slide (the old behaviour) made the panel redraw and the images pop in while it was moving.
   * @param {Event} event
   */
  static onExternalUpdate = (event) => {
    const update = /** @type {any} */ (event);
    update.promise
      ?.then(async (/** @type {any} */ result) => {
        if (result?.detail?.source === SOURCE) return;
        await GjCart.syncEngravingFee();
        const carts = /** @type {GjCart[]} */ ([...document.querySelectorAll('gj-cart')]);
        await Promise.allSettled(carts.map((cart) => cart.refresh()));
        const drawer = /** @type {any} */ (document.getElementById('GjCartDrawer'));
        const content = document.getElementById('GjCartDrawerContent');
        if (update.action !== 'add' || result?.detail?.didError) return;
        // The add came from a panel (quick add): close it first, so the minicart slides in alone
        await GjCart.closeOtherDrawers(drawer);
        if (drawer && content?.hasAttribute('data-auto-open')) {
          await GjCart.decodeImages(content);
          drawer.open();
        }
      })
      .catch(() => {});
  };

  /**
   * Close every open <gj-drawer> except `keep` and wait for their closing slide (at most 700 ms).
   * @param {Element | null} keep
   */
  static closeOtherDrawers(keep) {
    const open = /** @type {any[]} */ ([...document.querySelectorAll('gj-drawer[open]')]).filter((d) => d !== keep);
    return Promise.all(
      open.map(
        (other) =>
          new Promise((resolve) => {
            other.addEventListener('gj:drawer:close', resolve, { once: true });
            setTimeout(resolve, 700);
            other.close();
          })
      )
    );
  }

  /**
   * Load the minicart thumbnails before the drawer slides in (they are lazy, and a closed dialog loads nothing),
   * waiting at most `timeout` ms so a slow image never delays the drawer.
   * @param {Element} root @param {number} [timeout]
   */
  static decodeImages(root, timeout = 350) {
    const images = [...root.querySelectorAll('img')];
    for (const image of images) image.loading = 'eager';
    const decoded = Promise.allSettled(images.map((image) => image.decode()));
    return Promise.race([decoded, new Promise((resolve) => setTimeout(resolve, timeout))]);
  }

  /**
   * Fetch this element's sections and swap them in. The Section Rendering API can answer `null` for a section:
   * if even this request does not bring fresh HTML, reload the page rather than show a stale cart.
   */
  async refresh() {
    const sections = this.dataset.sections;
    if (!sections) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sections);
    const response = await fetch(url).then((r) => r.json());
    if (!this.#swap(response) && this.isConnected) window.location.reload();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-gj-line][data-gj-quantity]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    this.#change(
      { line: Number(button.dataset.gjLine), quantity: Number(button.dataset.gjQuantity) },
      button.dataset.variantId
    );
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-gj-gift')) return;
    const variantId = Number(input.dataset.variantId);
    const line = Number(input.dataset.line || 0);
    if (input.checked) {
      this.#request(route('cart_add_url') ?? '/cart/add.js', { items: [{ id: variantId, quantity: 1 }] }, 'add', [
        { merchandiseId: String(variantId), quantity: 1 },
      ]);
    } else if (line) {
      this.#change({ line, quantity: 0 }, String(variantId));
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-gj-discount')) return;
    event.preventDefault();
    const code = String(new FormData(form).get('discount') ?? '').trim();
    const remove = form.dataset.remove;
    const codes = remove !== undefined ? '' : code;
    if (remove === undefined && !code) return;
    this.#request(route('cart_update_url') ?? '/cart/update.js', { discount: codes }, 'update').then((cart) => {
      // Shopify keeps unknown codes out of the cart: tell the customer instead of failing silently
      const applied = (cart?.discount_codes ?? []).some(
        (/** @type {{ code: string, applicable: boolean }} */ c) => c.applicable && c.code.toLowerCase() === code.toLowerCase()
      );
      if (code && !applied) {
        const fresh = document.getElementById(this.id) ?? this;
        const error = fresh.querySelector('[data-gj-discount-error]');
        if (error) error.textContent = fresh.getAttribute('data-discount-error') ?? '';
      }
    });
  };

  /** @param {{ line: number, quantity: number }} change @param {string | undefined} variantId */
  #change(change, variantId) {
    const lines = variantId ? [{ merchandiseId: variantId, quantity: change.quantity }] : [];
    this.#request(route('cart_change_url') ?? '/cart/change.js', change, change.quantity === 0 ? 'remove' : 'update', lines);
  }

  /**
   * POST to the Ajax Cart API with this element's sections, announce the change (Horizon's cart count listens to
   * CartLinesUpdateEvent, which needs at least one line), swap the HTML. Resolves with the fresh cart.
   * @param {string} url @param {Record<string, unknown>} body @param {string} action
   * @param {Array<{ merchandiseId: string, quantity: number }>} [lines]
   */
  async #request(url, body, action, lines = []) {
    this.setAttribute('aria-busy', 'true');
    const deferred = CartLinesUpdateEvent.createPromise();
    if (lines.length > 0) {
      this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.dataset.sections, sections_url: window.location.pathname }),
      });
      const data = await response.json();
      const cart = await fetch(`${route('cart_url') ?? '/cart'}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: SOURCE, itemCount: cart.item_count, didError: Boolean(data.status) },
      });
      if (data.status) this.#announce(data.description || data.message);
      const feeChanged = await GjCart.syncEngravingFee();
      if (feeChanged || !data.sections || !this.#swap(data.sections)) await this.refresh();
      return feeChanged ? await fetch(`${route('cart_url') ?? '/cart'}.js`).then((r) => r.json()) : cart;
    } catch (error) {
      deferred.reject(error);
      this.#announce(this.dataset.errorText);
      return null;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /** @param {Record<string, string | null>} sections @returns {boolean} whether fresh HTML was swapped in */
  #swap(sections) {
    for (const html of Object.values(sections ?? {})) {
      if (!html) continue;
      const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(this.id);
      if (fresh) {
        this.replaceWith(document.importNode(fresh, true));
        return true;
      }
    }
    return false;
  }

  /** @param {string | undefined} text */
  #announce(text) {
    const region = this.querySelector('[data-gj-cart-live]');
    if (region && text) region.textContent = text;
  }
}

if (!customElements.get('gj-cart')) customElements.define('gj-cart', GjCart);
